import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { Globe, Eye, Edit3, Save } from "lucide-react";

interface StorefrontControlsProps {
  isEditing: boolean;
  hasUnsavedChanges: boolean;
  isPublishing: boolean;
  publishSlug: string;
  onToggleEditing: () => void;
  onSave: () => void;
  onPublish: () => void;
  onGenerateSlug: () => void;
  onPublishSlugChange: (slug: string) => void;
  primaryColor: string;
}

export function StorefrontControls({
  isEditing,
  hasUnsavedChanges,
  isPublishing,
  publishSlug,
  onToggleEditing,
  onSave,
  onPublish,
  onGenerateSlug,
  onPublishSlugChange,
  primaryColor,
}: StorefrontControlsProps) {
  const { t } = useTranslation();

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Globe className="w-5 h-5 text-gray-500" />
            <span className="text-sm text-gray-600">
              {t("storefront.publish.ready")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Input
              placeholder={t("storefront.publish.enterSlug")}
              value={publishSlug}
              onChange={(e) =>
                onPublishSlugChange(
                  e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-")
                )
              }
              className="w-48 h-9"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={onGenerateSlug}
              disabled={isPublishing}
            >
              {t("storefront.publish.generate")}
            </Button>
            <Button
              onClick={onPublish}
              disabled={!publishSlug.trim() || isPublishing}
              size="sm"
              style={{ backgroundColor: primaryColor }}
              className="text-white"
            >
              {isPublishing
                ? t("storefront.publish.publishing")
                : t("storefront.publish.publish")}
            </Button>
            <Button
              variant={isEditing ? "default" : "outline"}
              size="sm"
              onClick={onToggleEditing}
            >
              {isEditing ? (
                <Eye className="h-4 w-4 mr-2" />
              ) : (
                <Edit3 className="h-4 w-4 mr-2" />
              )}
              {isEditing ? "Preview" : "Edit"}
            </Button>
            {hasUnsavedChanges && (
              <Button size="sm" onClick={onSave}>
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
